import React, { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { getCourseProgress, toggleModule, savePosition, downloadCertificado, fileUrl, thumbUrl } from "@/lib/api";
import ContentBloque from "./ContentBloque";
import { ChevronRight } from "lucide-react";

const legacyToBloques = (l) => {
  const b = [];
  if (l.contenido) b.push({ tipo: "texto", texto: l.contenido });
  (l.videos || []).forEach((v) => {
    if (v.tipo === "upload" && v.file_url) b.push({ tipo: "video", file_url: v.file_url });
    else if (v.url) b.push({ tipo: v.tipo === "vimeo" ? "vimeo" : "youtube", url: v.url });
  });
  if (l.video_tipo && !(l.videos || []).length) {
    if (l.video_tipo === "upload" && l.video_file_url) b.push({ tipo: "video", file_url: l.video_file_url });
    else if (l.video_url) b.push({ tipo: l.video_tipo === "vimeo" ? "vimeo" : "youtube", url: l.video_url });
  }
  (l.imagenes || []).forEach((img) => b.push({ tipo: "imagen", file_url: img }));
  (l.recursos || []).forEach((r) => b.push({ tipo: r.tipo === "imagen" ? "imagen" : "pdf", file_url: r.url, titulo: r.nombre }));
  return b;
};

export default function Leccion({ slug, onBack }) {
  const [data, setData] = useState(null);
  const [done, setDone] = useState([]);
  const [activeKey, setActiveKey] = useState(null);

  const load = () => getCourseProgress(slug).then((d) => { setData(d); setDone(d.completed_modules || []); });
  useEffect(() => { load(); setActiveKey(null); /* eslint-disable-next-line */ }, [slug]);

  const { units, modules } = useMemo(() => {
    const units = [];
    const modules = [];
    (data?.course?.modulos || []).forEach((m, mi) => {
      const modUnits = [];
      if ((m.bloques || []).length || m.descripcion) {
        const u = { key: `mod-${mi}`, type: "mod", completeId: `mod-${mi}`, titulo: m.titulo, descripcion: m.descripcion || "", bloques: m.bloques || [], modIdx: mi };
        units.push(u); modUnits.push(u);
      }
      const subs = m.submodulos || [];
      if (subs.length === 0 && (m.lecciones || []).length) {
        // legacy
        (m.lecciones || []).forEach((l) => {
          const u = { key: `${m.orden}.${l.orden}`, type: "cap", completeId: `${m.orden}.${l.orden}`, titulo: l.titulo, descripcion: l.descripcion || "", bloques: legacyToBloques(l), modIdx: mi, subTitulo: l.submodulo || "" };
          units.push(u); modUnits.push(u);
        });
      }
      subs.forEach((s) => {
        if ((s.bloques || []).length || s.descripcion) {
          const u = { key: `sub-${s.id}`, type: "sub", completeId: `sub-${s.id}`, titulo: s.titulo, descripcion: s.descripcion || "", bloques: s.bloques || [], modIdx: mi, subTitulo: s.titulo };
          units.push(u); modUnits.push(u);
        }
        (s.capitulos || []).forEach((c) => {
          const u = { key: c.id, type: "cap", completeId: c.id, titulo: c.titulo, descripcion: c.descripcion || "", bloques: c.bloques || [], modIdx: mi, subTitulo: s.titulo };
          units.push(u); modUnits.push(u);
        });
      });
      modules.push({ m, mi, units: modUnits });
    });
    return { units, modules };
  }, [data]);

  const caps = units.filter((u) => u.completeId);
  const total = caps.length || 1;

  if (!data) return <p style={{ color: "var(--muted)" }}>Cargando…</p>;
  const curso = data.course;

  // Al entrar en un curso siempre se muestra el PRIMER módulo (arriba del todo)
  const currentKey = activeKey || units[0]?.key;
  const current = units.find((u) => u.key === currentKey) || units[0];
  const curIdx = units.findIndex((u) => u.key === current?.key);
  const nextUnit = curIdx >= 0 && curIdx < units.length - 1 ? units[curIdx + 1] : null;

  const openUnit = (key) => {
    setActiveKey(key);
    savePosition(slug, key).catch(() => {});
  };

  const toggle = async (completeId, modIdx) => {
    try {
      const before = done;
      const r = await toggleModule(slug, completeId);
      setDone(r.completed_modules);
      // insignia de módulo
      const mod = modules[modIdx];
      if (mod) {
        const ids = mod.units.filter((u) => u.completeId).map((u) => u.completeId);
        const allNow = ids.length > 0 && ids.every((x) => r.completed_modules.includes(x));
        const allBefore = ids.every((x) => before.includes(x));
        if (allNow && !allBefore) { toast.success(`🏅 ¡Insignia desbloqueada! Módulo ${mod.mi + 1} completado`); return; }
      }
      if (r.completed_modules.includes(completeId)) toast.success("Capítulo completado ✓");
    } catch { toast.error("No se pudo guardar el progreso."); }
  };

  const earnedBadges = modules.filter((mo) => {
    const ids = mo.units.filter((u) => u.completeId).map((u) => u.completeId);
    return ids.length > 0 && ids.every((x) => done.includes(x));
  });

  const completado = caps.length > 0 && done.filter((d) => caps.some((c) => c.completeId === d)).length >= total;
  const bajarCert = async () => {
    try {
      const blob = await downloadCertificado();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "Certificado-Liberti-Club-IA.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("¡Certificado descargado! 🎓");
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Completa el 100% del Máster para tu certificado." : "No se pudo generar el certificado.");
    }
  };

  const doneCaps = done.filter((d) => caps.some((c) => c.completeId === d)).length;

  return (
    <>
      <button className="back" onClick={onBack} data-testid="leccion-back">← El Máster</button>
      <div className="main-head">
        <div>
          <span className="chip">{curso.categoria}</span>
          <h1 style={{ marginTop: 12 }}>{curso.titulo}</h1>
          <p className="sub" data-testid="leccion-progreso">{doneCaps} de {total} apartados completados</p>
        </div>
        {completado && (
          <button className="btn btn-gold btn-sm" data-testid="descargar-certificado" onClick={bajarCert}>🎓 Descargar certificado</button>
        )}
      </div>
      {earnedBadges.length > 0 && (
        <div data-testid="insignias" style={{ display: "flex", gap: 12, flexWrap: "wrap", margin: "4px 0 20px" }}>
          {earnedBadges.map((mo) => (
            <div key={mo.mi} data-testid={`insignia-${mo.mi + 1}`} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 14px", borderRadius: 999, border: "1px solid var(--gold)", background: "rgba(197,164,90,0.12)" }}>
              <span style={{ fontSize: 16 }}>🏅</span>
              <span style={{ fontSize: 12, color: "var(--gold)", fontWeight: 600 }}>Módulo {mo.mi + 1} completado</span>
            </div>
          ))}
        </div>
      )}

      {units.length === 0 ? (
        <div style={{ color: "var(--muted)", padding: "40px 0" }} data-testid="master-vacio">
          Este curso todavía no tiene contenido. Vuelve pronto ✨
        </div>
      ) : (
        <div className="lesson">
          <div>
            {current && (
              <div style={{ fontSize: 11, letterSpacing: ".16em", textTransform: "uppercase", color: "var(--gold)", marginBottom: 12 }} data-testid="leccion-modulo-actual">
                Módulo {current.modIdx + 1}{current.subTitulo ? ` · ${current.subTitulo}` : ""}
              </div>
            )}
            <h2 className="serif" style={{ fontSize: 22, fontWeight: 600, margin: "0 0 16px" }}>{current?.titulo}</h2>
            {current?.descripcion && (
              <p data-testid="leccion-descripcion" style={{ color: "var(--cream)", opacity: .85, fontSize: 15, lineHeight: 1.8, fontWeight: 300, whiteSpace: "pre-wrap", margin: "0 0 20px", maxWidth: 720 }}>
                {current.descripcion}
              </p>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 18, maxWidth: 720 }} data-testid="leccion-bloques">
              {(current?.bloques || []).map((b, i) => <ContentBloque key={i} b={b} idx={i} />)}
              {(current?.bloques || []).length === 0 && !current?.descripcion && <p style={{ color: "var(--muted)" }}>Sin contenido en este apartado.</p>}
            </div>
            {current?.completeId && (
              <div style={{ display: "flex", gap: 12, marginTop: 24, flexWrap: "wrap" }}>
                <button className="btn btn-gold btn-sm" data-testid="leccion-marcar"
                  onClick={() => toggle(current.completeId, current.modIdx)}>
                  {done.includes(current.completeId) ? "Marcar como pendiente" : "Marcar como completado ✓"}
                </button>
                {nextUnit && (
                  <button className="abtn" data-testid="leccion-continuar"
                    onClick={() => { openUnit(nextUnit.key); document.querySelector(".main")?.scrollTo({ top: 0, behavior: "smooth" }); }}
                    style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    Continuar <ChevronRight size={16} />
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="mods" data-testid="leccion-indice">
            {modules.map((mo) => {
              const capIds = mo.units.filter((u) => u.completeId).map((u) => u.completeId);
              const dc = capIds.filter((x) => done.includes(x)).length;
              const pctMod = capIds.length ? Math.round((dc / capIds.length) * 100) : 0;
              let lastSub = null;
              return (
                <div key={mo.mi} style={{ marginBottom: 16 }} data-testid={`modulo-bloque-${mo.mi + 1}`}>
                  {mo.m.portada && (
                    <div data-testid={`modulo-portada-${mo.mi + 1}`}
                      style={{ backgroundImage: `url(${thumbUrl(mo.m.portada, 500)})`, backgroundSize: "cover", backgroundPosition: "center", height: 84, borderRadius: 8, marginBottom: 8, border: "1px solid var(--line)" }} />
                  )}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "0 4px 6px" }}>
                    <span style={{ fontSize: 11, letterSpacing: ".12em", textTransform: "uppercase", color: "var(--gold)", fontWeight: 600 }}>
                      Módulo {mo.mi + 1}{mo.m.titulo ? `: ${mo.m.titulo}` : ""}
                    </span>
                    <span style={{ fontSize: 11, color: "var(--muted)" }} data-testid={`modulo-progreso-${mo.mi + 1}`}>{dc}/{capIds.length}</span>
                  </div>
                  <div style={{ height: 4, background: "rgba(255,255,255,0.08)", borderRadius: 4, margin: "0 4px 10px", overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${pctMod}%`, background: "var(--gold)", transition: "width .3s" }} />
                  </div>
                  {mo.units.map((u) => {
                    const showSub = u.subTitulo && u.subTitulo !== lastSub && u.type !== "mod";
                    if (u.subTitulo) lastSub = u.subTitulo;
                    const isDone = done.includes(u.completeId);
                    const isActive = u.key === currentKey;
                    const label = u.type === "cap" ? u.titulo : (u.type === "mod" ? `📄 ${u.titulo || "Introducción"}` : `📄 ${u.titulo || "Introducción"}`);
                    return (
                      <React.Fragment key={u.key}>
                        {showSub && <div className="submod-head" data-testid={`submodulo-head-${u.subTitulo}`}>{u.subTitulo}</div>}
                        <button data-testid={`unit-item-${u.key}`} className={`mod ${isDone ? "done" : ""} ${isActive ? "now" : ""}`} onClick={() => openUnit(u.key)}>
                          <span className="st">{isDone ? "✓" : "▸"}</span>{label}
                        </button>
                      </React.Fragment>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}
